import React, { FC, useContext } from 'react';
import { NavLink, useLocation, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import { useSelector } from 'react-redux';
import { Typography } from '@material-ui/core';
import SVG from '../assets';
import { useAppConfiguration } from '../../hook/useAppConfiguration';
import { AuthContext } from '../../../shared/app/AuthProvider';
import { LanguageItem } from '../LanguageItem';
import { useStyles } from './styles';
import { selectUser, useAppDispatch, patchUser } from '../../../../store';
import { MenuButtonProps } from '../MenuDesktop/interface';

const MenuButton: FC<MenuButtonProps> = ({ Icon, label, active }) => {
  const classes = useStyles({ active });

  return (
    <Box className={classes.menuButton}>
      <IconButton className={classes.menuIcon} size="medium">
        <Icon />
      </IconButton>
      <Typography className={classes.menuLabel} variant="subtitle1">
        {label}
      </Typography>
    </Box>
  );
};

const MenuMobile = () => {
  const classes = useStyles({});
  const [t, i18n] = useTranslation();
  const location = useLocation();
  const { authUser } = useContext(AuthContext);
  const { user } = useSelector(selectUser);
  const dispatch = useAppDispatch();
  const { withSideNavigation } = useAppConfiguration();

  if (!authUser) {
    return <Redirect to="/login" />;
  }

  const dashboardPages = ['/', '/user-journey', '/mentors'];

  const onLanguageChange = (language: string) => {
    dispatch(patchUser({ locale: language }));
    if (window.analytics && user) {
      window.analytics.identify(user?.id, {
        language,
      });
    }
  };

  return (
    <>
      {withSideNavigation && (
        <Box display="table" width="100%" height="85%">
          <>
            <Box display="table-row">
              <Box display="table-cell" height="100%">
                <Box className={classes.menuItem}>
                  <NavLink
                    className={classes.menuLink}
                    activeClassName={classes.menuLinkActive}
                    to="/"
                    title={t('Dashboard.title')}
                    exact
                    isActive={(_, { pathname }) => {
                      if (dashboardPages.includes(pathname)) {
                        return true;
                      }
                      if (/\/topic\//.test(pathname)) {
                        return true;
                      }
                      return false;
                    }}
                  >
                    <MenuButton
                      active={dashboardPages.includes(location.pathname)}
                      label={t('Dashboard.title')}
                      Icon={SVG.Dashboard}
                    />
                  </NavLink>
                  {dashboardPages.includes(location.pathname) && (
                    <Box className={classes.indicator} />
                  )}
                </Box>
                <Box className={classes.menuItem}>
                  <NavLink
                    className={classes.menuLink}
                    activeClassName={classes.menuLinkActive}
                    to="/bookings"
                    title={t('Menu.bookings')}
                    exact
                    isActive={(_, { pathname }) => {
                      if (pathname === '/bookings') {
                        return true;
                      }
                      return false;
                    }}
                  >
                    <MenuButton
                      active={location.pathname === '/bookings'}
                      label={t('Menu.bookings')}
                      Icon={SVG.Bookings}
                    />
                  </NavLink>
                  {location.pathname === '/bookings' && (
                    <Box className={classes.indicator} />
                  )}
                </Box>
                <Box className={classes.menuItem}>
                  <NavLink
                    className={classes.menuLink}
                    activeClassName={classes.menuLinkActive}
                    to="/exercises"
                    title="Exercices"
                    exact
                    isActive={(_, { pathname }) => {
                      if (pathname === '/exercises') {
                        return true;
                      }
                      return false;
                    }}
                  >
                    <MenuButton
                      active={location.pathname === '/exercises'}
                      label={t('Menu.exercises')}
                      Icon={SVG.Exercises}
                    />
                  </NavLink>
                  {location.pathname === '/exercises' && (
                    <Box className={classes.indicator} />
                  )}
                </Box>
                <Box className={classes.menuItem}>
                  <NavLink
                    className={classes.menuLink}
                    activeClassName={classes.menuLinkActive}
                    to="/account"
                    title={t('Menu.account')}
                    exact
                    isActive={(_, { pathname }) => {
                      if (pathname === '/account') {
                        return true;
                      }
                      if (pathname === '/account/payment') {
                        return true;
                      }
                      return false;
                    }}
                  >
                    <MenuButton
                      active={location.pathname === '/account'}
                      label={t('Menu.account')}
                      Icon={SVG.Profile}
                    />
                  </NavLink>
                  {location.pathname === '/account' && (
                    <Box className={classes.indicator} />
                  )}
                </Box>
              </Box>
            </Box>
          </>
        </Box>
      )}
      <Box
        className={classes.languageContainer}
        id="language-selector"
        pb={3}
        fontSize={16}
      >
        {['en', 'de']
          .map((lang) => (
            <LanguageItem
              key={`${lang}-key`}
              language={lang}
              i18n={i18n}
              onLanguageChange={onLanguageChange}
            />
          ))
          // @ts-ignore next
          .reduce((prev, curr) => [
            prev,
            <Box className={classes.divider}>
              <SVG.Divider />
            </Box>,
            curr,
          ])}
      </Box>
      {withSideNavigation && (
        <Box>
          <NavLink
            className={classes.menuLink}
            activeClassName={classes.menuLinkActive}
            to="/logout"
            title={t('Menu.logOut')}
            exact
          >
            <MenuButton label={t('Menu.logOut')} Icon={SVG.Logout} />
          </NavLink>
        </Box>
      )}
    </>
  );
};

export default MenuMobile;
